import { ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { find, uniqBy } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { BudgetModel, GetCustomerSettingsResponse } from '../../../../../Generated/Commodity-Management-Api';
import { StoneXGrid } from '../../../../StoneX';
import { StoneXNumber } from '../../../../StoneXMui';
import { BudgetFilters, BudgetRow } from '../Types';
import { MoreVert } from '@mui/icons-material';
import { ClickAwayListener, IconButton, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import dayjs from 'dayjs';
import { ContractDateHelper } from '../../../../../Helpers';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useApplicationSelector, RootState } from "../../../../../Redux/ReduxStore";
import { CommodityManagementApi } from '../../../../../Apis/Apis';

interface BudgetTableProps {
  budgets: BudgetRow[];
  onViewAuditTrail: (budgetId: number) => void;
  onEditBudget: (budgetFilter: BudgetFilters) => void;
  defaultExpanded?: boolean;
}

export default function BudgetTable(props: BudgetTableProps) {
    const { budgets, onViewAuditTrail, onEditBudget, defaultExpanded } = props;

  const gridRef = useRef<AgGridReact<BudgetModel>>(null);
    const navigate = useNavigate();
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

  const columns = useMemo(() => {
    const components = uniqBy(
      budgets.flatMap((x) => x.budgetPricingStrategy?.components ?? []),
      (x) => x!.componentType,
    );

    const pricingComponents =
      components.length > 1
        ? components.map((q) => {
            return {
              headerName: q.componentType!,
              flex: 1,
              cellClass: 'justify-right', 
              filter: 'agNumberColumnFilter', 
              cellRenderer: (params: ICellRendererParams) => {
                const price = find(params.data.budgetPrices, (r) => r.componentType == q.componentType)?.price ?? 0;
                return <StoneXNumber number={price} decimals={params.data.commodity.displayPrecision ?? 3} /> 
              },
            };
          })
        : [{ 
            headerName: 'Price', 
            field: 'price', 
            cellClass: 'justify-right', 
            filter: 'agNumberColumnFilter', 
            flex: 1,
            cellRenderer: (params: ICellRendererParams) => {
              return <StoneXNumber number={params.value} decimals={params.data.commodity.displayPrecision ?? 3} /> 
            },
        }];

    const columns: ColDef[] = [
      { headerName: 'Commodity', field: 'commodity.commodityName', flex: 1, filter: 'agSetColumnFilter', sort: 'asc', sortIndex: 0 },
        { headerName: 'Counterparty', field: 'counterparty.counterpartyName', flex: 1, filter: 'agSetColumnFilter', hide: !customerSettings.budgetsByCounterparty },
      { headerName: 'Location', field: 'location.locationName', flex: 1, filter: 'agSetColumnFilter', hide: !customerSettings.budgetsByLocation, sort: 'asc', sortIndex: 1 },
      {
        headerName: 'Budget Year',
        // field: 'budgetDate.year',
        hide: true,
        sort: 'asc',
        rowGroup: true,
        valueGetter: (params: ValueGetterParams) => ContractDateHelper.getFiscalYear(params.data.budgetDate, customerSettings.fiscalMonthOffset!),
      },
      {
        headerName: 'Budget Month',
        field: 'budgetDate.label',
        flex: 1,
        filter: 'agDateColumnFilter',
        sort: 'desc',
        sortIndex: 2,
        comparator: (_valueA, _valueB, nodeA, nodeB) =>
          ContractDateHelper.sortAscending(nodeA.data?.budgetDate, nodeB.data?.budgetDate),
      },
      { headerName: 'Volume', field: 'volume', cellClass: 'justify-right', filter: 'agNumberColumnFilter', flex: 1, cellRenderer: (params: ICellRendererParams) => <StoneXNumber number={params.value} decimals={0} /> },
      ...pricingComponents,
      { headerName: 'Unit', field: 'unit', flex: 1, filter: 'agSetColumnFilter' },
      { headerName: 'Currency', field: 'currency', flex: 1, filter: 'agSetColumnFilter' },
      {
        colId: 'actions',
        headerName: '',
        width: 40,
        resizable: false,
        cellStyle: { padding: '0' },
        sortable: false,
        suppressAutoSize: true,
        suppressMovable: true,
        cellRenderer: (params: ICellRendererParams<BudgetRow>) => {

            const [open, setOpen] = useState(false);
            const anchorRef = useRef<HTMLButtonElement | null>(null);

            return <div>
              <IconButton ref={anchorRef} sx={{borderRadius: 1}} onClick={() => setOpen(true)}><MoreVert /></IconButton>
              
              <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" >
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                  <Paper>
                    <MenuList autoFocusItem={open}>
                      <MenuItem onClick={() => editBudgets(params.data!.budgetDate!.year!, params.data!.commodity!.commodityId, params.data!.location?.locationId, params.data!.counterparty?.counterpartyId, params.data!.budgetRoundModel?.budgetRoundId)}>Edit Fiscal Year</MenuItem>
                      <MenuItem onClick={() => onViewAuditTrail(params.data!.budgetId!)}>Audit</MenuItem>
                    </MenuList>
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </div>
        },
      },
    ];

    if (customerSettings.positionContractDates) {
        const budgetMonthIndex = columns.findIndex(c => c.headerName === 'Budget Month');
        if (budgetMonthIndex !== -1) {
            columns.splice(budgetMonthIndex + 1, 0, { headerName: 'Contract Month', field: 'contractDate.label', flex: 1, filter: 'agDateColumnFilter' });
        }
    }

    if (customerSettings.usesBudgetRounds) {
        const budgetMonthIndex = columns.findIndex(c => c.headerName === 'Budget Month');
        if (budgetMonthIndex !== -1) {
            columns.splice(budgetMonthIndex + 1, 0, { headerName: 'Budget Round', field: 'budgetRoundModel.budgetRoundName', flex: 1, filter: 'agSetColumnFilter' });
        }
    }

    return columns;
  }, [budgets, customerSettings]);

  function editBudgets(year: number, commodityId: number, locationId?: number, counterpartyId?: number, budgetRoundId?: number) {
    onEditBudget({
      fiscalYear: year,
      commodityId: commodityId,
      locationId: locationId,
      counterpartyId: counterpartyId,
      budgetRoundId: budgetRoundId,
    }); 
  }

  const gridOptions: AgGridReactProps = useMemo(() => {
    return {
      rowGroupPanelShow: 'never',
      enableRangeSelection: true,
      groupDisplayType: "groupRows",
      groupDefaultExpanded: defaultExpanded ? 1 : 0,
      defaultColDef: {
        sortable: true,
        resizable: true,
      },
      groupRowRendererParams: {
        suppressCount: true,
        comparator: (valueA: any, valueB: any) => {
          if (valueA == null) return 1;
          if (valueB == null) return -1;
          return ContractDateHelper.sortAscending(valueA, valueB);
        },
        innerRenderer: (params: any) => {
          const shortMonthYear = params.value; 
          const date = dayjs(shortMonthYear, "MMM YY");
          return date.isValid() ? date.format("MMMM YYYY") : shortMonthYear; 
        },
      },
    };
  }, []);

  return <StoneXGrid rows={props.budgets} columns={columns} getRowId={x => x.data.budgetId}  gridOptions={gridOptions} onlyShowGroupLabels />;
}
